<template>
  <div class="forwards-fill-bar">
    <StackedProgressBar
      :segments="segments"
      :total="total"
      :ariaLabel="barLabel"
    />
    <div v-if="showCaption" class="text-body-tertiary fs-10 mt-1">
      {{ caption }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import StackedProgressBar from "@/components/common/StackedProgressBar.vue";
import type { StackedProgressBarSegment } from "@/components/common/stackedProgressBarTypes";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

/**
 * How much of a committed volume is already in hand.
 *
 * `escrowed` and `delivered` are parts of `total`, not siblings of it — the same relationship
 * `PeriodSummary` states between `escrowed` and `committed`. The remainder is left as the bar's
 * empty track unless `showShortfall` stripes it, which is how a delivery that will not be covered
 * is marked without turning the whole bar red.
 */
const props = withDefaults(
  defineProps<{
    escrowed: number;
    total: number;
    /** Already shipped to the buyer; drawn ahead of the escrowed part. */
    delivered?: number;
    /** Hatch the gap between what is in hand and the total. */
    showShortfall?: boolean;
    /** Replaces the generated "x of y MWh" line; pass an empty string to drop the caption. */
    caption?: string | null;
  }>(),
  { delivered: 0, showShortfall: false, caption: null }
);

const { t } = useI18n();

const inHand = computed(() => props.escrowed + props.delivered);

const segments = computed<StackedProgressBarSegment[]>(() => [
  {
    key: "delivered",
    value: props.delivered,
    variant: "success",
    title: t("forwards.common.delivered"),
  },
  {
    key: "escrowed",
    value: props.escrowed,
    variant: "info",
    title: t("forwards.common.escrowed"),
  },
  ...(props.showShortfall
    ? [
        {
          key: "shortfall",
          value: Math.max(0, props.total - inHand.value),
          variant: "warning-stripe" as const,
          title: t("forwards.common.shortfall"),
        },
      ]
    : []),
]);

const generatedCaption = computed(() =>
  t("forwards.common.fill_of_total", {
    filled: formatNumberWithSpaces(inHand.value),
    total: formatNumberWithSpaces(props.total),
  })
);

const caption = computed(() => props.caption ?? generatedCaption.value);
const showCaption = computed(() => caption.value !== "");
const barLabel = computed(() => generatedCaption.value);
</script>
